import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, errorMessage } from '../api/client';
import { MACHINES_KEY } from './machines/MachineActions';

export const RECIPES_KEY = ['recipes'] as const;

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function EmptyRecipes() {
  return (
    <div className="empty">
      <p className="empty-title">No workflows yet.</p>
      <p className="muted">
        Start one with <Link to="/workflows/new">New workflow</Link>, or ask <Link to="/claude">Claude</Link> — here in Couch, or in
        Claude's own app with Couch connected (<Link to="/account">Account → Connected apps</Link>). The workflows it builds show up here.
      </p>
    </div>
  );
}

/** Recipes friends shared (doc 30); hidden when there are none. */
function SharedWithYou() {
  const shared = useQuery({ queryKey: ['shared-recipes'], queryFn: api.sharedRecipes });
  const list = shared.data?.recipes ?? [];
  if (list.length === 0) return null;
  return (
    <section className="panel" aria-labelledby="shared-with-you">
      <h2 id="shared-with-you">Shared with you</h2>
      <ul className="share-list">
        {list.map((r) => (
          <li key={r.id}><Link to={`/shared/${r.id}`}>{r.title}</Link> <span className="muted">— from {r.sharedBy.displayName}, {when(r.sharedAt)}</span></li>
        ))}
      </ul>
    </section>
  );
}

export function RecipesPage() {
  const recipes = useQuery({ queryKey: RECIPES_KEY, queryFn: api.listRecipes });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const machineName = (id: string | null) => machines.data?.machines.find((m) => m.id === id)?.name ?? '—';
  return (
    <section aria-labelledby="recipes-title">
      <div className="page-head"><h1 id="recipes-title">Workflows</h1><Link className="button-link" to="/workflows/new">New workflow</Link></div>
      {recipes.isPending && <p role="status">Loading…</p>}
      {recipes.isError && <p role="alert" className="form-error">{errorMessage(recipes.error)}</p>}
      {recipes.data?.recipes.length === 0 && <EmptyRecipes />}
      {recipes.data && recipes.data.recipes.length > 0 && (
        <div className="table-scroll">
          <table className="recipe-table">
            <thead><tr><th>Workflow</th><th>Made by</th><th>Last run on</th><th>Version</th><th>Changed</th></tr></thead>
            <tbody>
              {recipes.data.recipes.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/workflows/${r.id}`}>{r.title}</Link>{r.intent && <div className="muted">{r.intent}</div>}</td>
                  <td><span className="badge">{r.author === 'ai' ? 'AI' : 'You'}</span></td>
                  <td>{machineName(r.machineId)}</td>
                  <td>v{r.version}</td>
                  <td>{when(r.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SharedWithYou />
    </section>
  );
}
